import { closePopup, getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import classNames from "classnames";
import Modal from 'components/base/Modal';
import { Form, Formik } from 'formik';
import numberFormat from 'utils/number/format';
import currencyFormat from 'utils/currency/numberToPrice';
import { getBrandTermsUrl } from 'utils/brand';
import Checkbox from 'components/base/Checkbox';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getPlanDetails, upgradeDowngradeManagePlan, getUsageSectionAPI, getPrePaidCreditsSectionAPI } from '../../../data/user/actions';
import { getLeadAutomatorAPI } from '../../../data/user/actions';

import { selectCardList, selectPlanDetails, selectUserId, selectVendorEmailAddress, selectVendorPhoneNumber, selectLeadAutomator, isLegacyAccountPlan } from '../../../data/user/selectors';
import CreditCardDropdown from '../components/CreditCardDropdown';
import CVV99Modal from "../components/cvv99Modal";
import css from "../style.scss";
import LeaveMessagePopup from './LeaveMessage';
import { getPlanNameFromDetails } from '../Sections/LeadAutomator';
import { updateTrial14LeadAutomator } from '../../../data/user';

const ActivateSubscriptionPopup = (props) => {
  const cardList = useSelector(selectCardList);
  const userId = useSelector(selectUserId);
  const vendorPhoneNumber = useSelector(selectVendorPhoneNumber);
  const vendorEmailAddress = useSelector(selectVendorEmailAddress);
  const [showCVVModal, setShowCVVModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const dispatch = useDispatch();
  const [selectedCard, setSelectedCard] = useState(cardList[0] || {});
  const planDetails = useSelector(selectPlanDetails)?.toJS();
  const selectLeadAutomatorSection = useSelector(selectLeadAutomator)?.toJS();
  const isLegacyAccount = useSelector(isLegacyAccountPlan);
  const [activationSuccess, setActivationSuccess] = useState(false);

  // Check if this activation is from Call Settings flow
  const upgradedFromCallSettings = props.upgradedFromCallSettings || false;
  const onNavigate = props.onNavigate; // Navigation callback from parent

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
  };
  const handleClose = () => {
    props.closePopup();
    props.onClose && props.onClose();
  };

  const handleCardChange = (selectedOption) => {
    setSelectedCard(selectedOption);
  };

  const handleSubmit = async (values) => {
    const payload = {
      userId,
      currentPlanId: planDetails?.accountEnrolledPlans[0]?.planId,
      approvedAmount: planDetails?.accountEnrolledPlans[0]?.amount,
      activate: true,
      paymentMethodId: selectedCard?.paymentMethodId,
      savesAndExports: planDetails?.accountEnrolledPlans[0]?.quantity,
    }
    if (Number(planDetails?.accountEnrolledPlans[0]?.amount) > 99) {
      setShowCVVModal(true);
      return;
    }
    submitPrepaidCredits(payload);
  }

  const handleSubmitCVV = async (values, cvvValues) => {
    const payload = {
      userId,
      currentPlanId: planDetails?.accountEnrolledPlans[0]?.planId,
      approvedAmount: planDetails?.accountEnrolledPlans[0]?.amount,
      activate: true,
      paymentMethodId: selectedCard?.paymentMethodId,
      savesAndExports: planDetails?.accountEnrolledPlans[0]?.quantity,
      cardCode: cvvValues.cardCode,
    }
    submitPrepaidCredits(payload);
  }

  // Check if plan includes Lead Automator after successful activation from Call Settings flow
  useEffect(() => {
    if (activationSuccess && upgradedFromCallSettings) {
      // Wait for plan details to be fully refreshed
      const checkPlan = setTimeout(() => {
        // Check if plan details are available and trial is no longer available (activation successful)
        if (planDetails?.accountEnrolledPlans?.length > 0 && !planDetails?.trialAvailable) {
          const planName = getPlanNameFromDetails(planDetails);
          const planIncludesLeadAutomator = planName?.includes("Pro") || planName?.includes("Elite");
          const hasLeadAutomatorSubscription = selectLeadAutomatorSection?.accountPlanMrcs?.length > 0;
          
          // Check if plan already includes Lead Automator
          if (planIncludesLeadAutomator || hasLeadAutomatorSubscription) {
            // Plan already includes Lead Automator - just show success and close
            dispatch(LeaveMessagePopup.open({
              title: "Success",
              message: "Your subscription has been activated successfully.",
              yesLable: "Ok",
              cancelLable: null,
              CancelRedirect: () => null,
              yesRedirect: () => null
            }))
            props.closePopup();
            props.onSuccess && props.onSuccess();
            setShowCVVModal(false);
            dispatch(getUsageSectionAPI(userId))
            dispatch(getPrePaidCreditsSectionAPI(userId))
          } else {
            if (!planDetails?.usedLATrial && !planDetails?.trialAvailable && planName?.includes("Essentials Monthly")) {
              dispatch(updateTrial14LeadAutomator(true));
            }
            
            // Close modal first, then navigate
            props.closePopup();
            props.onClose && props.onClose();

            // Use setTimeout to ensure modal closes before navigation
            setTimeout(() => {
              if (isLegacyAccount) {
                // Show popup for legacy accounts
                dispatch(LeaveMessagePopup.open({
                  title: "Upgrade Your Account",
                  message: "Upgrade your account to Essentials, Pro, or Elite by clicking Upgrade Plan to continue.",
                  autoWidth: false,
                  yesLable: "Upgrade Plan",
                  cancelLable: "Cancel",
                  CancelRedirect: () => null,
                  yesRedirect: () => {
                    if (onNavigate) {
                      onNavigate('/accountnew/plan');
                    }
                  },
                }));
              } else {
                if (onNavigate) {
                  onNavigate('/accountnew/leadautomator');
                }
              }
            }, 100);
          }
        }
        setActivationSuccess(false);
      }, 500);
      
      return () => clearTimeout(checkPlan);
    }
  }, [activationSuccess, planDetails, selectLeadAutomatorSection]);

  const submitPrepaidCredits = async (payload) => {
    try {
      const response = await upgradeDowngradeManagePlan("upgrade", payload);
      if (response.success) {
        // Refresh plan details and Lead Automator data
        dispatch(getPlanDetails(userId));
        dispatch(getLeadAutomatorAPI(userId));

        // Check if this is from Call Settings flow
        if (upgradedFromCallSettings) {
          setActivationSuccess(true);
        } else {
          // Not from Call Settings flow - normal success flow
          dispatch(LeaveMessagePopup.open({
            title: "Success",
            message: response?.data?.data?.message,
            yesLable: "Ok",
            cancelLable: null,
            CancelRedirect: () => null,
            yesRedirect: () => null
          }))
          props.closePopup();
          props.onSuccess && props.onSuccess();
          setShowCVVModal(false);
          dispatch(getUsageSectionAPI(userId))
          dispatch(getPrePaidCreditsSectionAPI(userId))
        }
      } else {
        console.error(`activate subscription failed:`, response.error);
        setShowCVVModal(false);
        dispatch(LeaveMessagePopup.open({
          title: "Error",
          message: response.error,
          yesLable: "Ok",
          cancelLable: null,
          CancelRedirect: () => null,
          yesRedirect: () => null
        }))
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      setShowCVVModal(false);
      dispatch(LeaveMessagePopup.open({
        title: "Error",
        message: response.error,
        yesLable: "Ok",
        cancelLable: null,
        CancelRedirect: () => null,
        yesRedirect: () => null
      }))
    }
  }

  return (
    <Modal className={classNames(css.clsAccPageModals, css.clsBillingModal, css.clsAccPrepaidCreditsModal)} isOpen uniqId="ActivateSubscriptionPopup" width="auto" padding="0px 0px 0px" onClose={handleClose}>
      <div className={classNames(css.modal_lg, "modal-dialog modal-xl m-0")}>
        <div className={classNames(css.clsModalContent, "modal-content")}>
          <div className={classNames(css.clsModalContentBody, "modal-body")}>
            <div className={css.clsModalHeader}>
              <h2 className={css.clsModalTitle}>Activate Subscription</h2>
            </div>
            <Formik
              initialValues={{}}
              onSubmit={(values, { setSubmitting }) => {
                handleSubmit(values, () => {
                  setSubmitting(false);
                })
              }}
            >
              {({ handleSubmit, values, isSubmitting, setSubmitting }) => (
                <Form onSubmit={handleSubmit}>
                  <div className={css.clsModalHeader} style={{ padding: "0px" }}>
                    <h5 className={classNames(css.clsModalSubTitle, css.clsReloadSubTitle)}>
                      Your credit card will be billed {currencyFormat(planDetails?.accountEnrolledPlans[0]?.amount)}.
                      Once complete, your subscription will begin and you will have access to the full {numberFormat(planDetails?.accountEnrolledPlans[0]?.quantity)} saved property limit.
                    </h5>
                  </div>

                  <div className={classNames(css.clsModalBody)} style={{ padding: "0px" }}>
                    <div className={classNames(css.clsModalBodyInner, "clearfix w-100")}>
                      <div className={classNames("clearfix")}>
                        <div className={classNames(css.clsFormGroup, css.pl_16, css.pr_16, css.pt_32)}>
                          <p className={classNames(css.clsFormLabel, css.autoWidth)}> Select Credit Card</p>
                          <CreditCardDropdown
                            tag={false}
                            options={cardList}
                            onChange={(selectedOption) => handleCardChange(selectedOption)}
                          />
                        </div>
                      </div>

                      <div className={css.disclaimer}>
                        <Checkbox onChange={handleTermsChange} checked={termsAccepted} name="termsAccepted" />
                          <div className={css.clsTermsText}>
                            I agree to the <a href={getBrandTermsUrl()} target="terms">Terms and Conditions</a> and understand that
                            <span>I will be billed {currencyFormat(planDetails?.accountEnrolledPlans[0]?.amount)} per {planDetails?.accountEnrolledPlans[0]?.currentAnnual ? 'year' : 'month'} on a {planDetails?.accountEnrolledPlans[0]?.currentAnnual ? 'year-to-year' : 'month-to-month'} basis until I cancel my subscription.</span>
                            To cancel at any time, please email customer support at {vendorEmailAddress} to discontinue your subscription. For all other inquiries, please contact us at {vendorPhoneNumber}.
                          </div>
                      </div>
                    </div>
                  </div>

                  <div className={classNames(css.clsModalFooter)}>
                    <button className={classNames(css.btn_lg, css.btn_primary, css.text_primary,)} onClick={handleClose}> Cancel</button>
                    <button className={classNames(css.btn_lg, css.clsBtnOrng)} disabled={(isSubmitting || !termsAccepted)} type="submit">Submit Payment </button>
                  </div>
                  {showCVVModal && <CVV99Modal
                      showCVVModal={showCVVModal} cardEnding={selectedCard?.cardEnding} cvvLength={selectedCard?.cardCVVLength} handleSubmitCVV={(cvvValues) => handleSubmitCVV(values, cvvValues)}
                      handleCancel={() => {setShowCVVModal(false); setSubmitting(false)}} handleClose={() => {setShowCVVModal(false); setSubmitting(false)}}  />}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </Modal >


  );
};


const registrationId = getPopupRegistration(ActivateSubscriptionPopup);
ActivateSubscriptionPopup.open = (props = {}) => openPopup(registrationId, { ...props, priority: Priority.MEDIUM });
ActivateSubscriptionPopup.close = () => closePopup({ popup: registrationId });

export default ActivateSubscriptionPopup;
